import { useMemo } from 'react';

import { Button } from '@bambi/component-library/src/components/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';

import { DataGrid } from 'src/common/DataGrid/DataGrid';
import {
  PreferredDriverRead,
  useDriversListQuery,
  usePassengersPreferredDriversListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { useModal } from 'src/common/modals/useModal';
import { SectionHeading } from 'src/common/primitives/Section/SectionHeading';
import { formatFullName } from 'src/common/util/formatFullName';

import { createColumns } from './columns';
import { PreferredDriverEmptyState } from './PreferredDriverEmptyState';
import { PreferredDriverFormModal } from './PreferredDriverFormModal';

interface PreferredDriverDataGridProps {
  passengerId: string;
}

export function PreferredDriverDataGrid({
  passengerId,
}: PreferredDriverDataGridProps) {
  const { open, setOpen, openModal } = useModal();

  const { data: preferences, isLoading: isLoadingPreferences } =
    usePassengersPreferredDriversListQuery({ passengerId });

  const { data: drivers, isLoading: isLoadingDrivers } = useDriversListQuery(
    {}
  );

  const driverNameLookup = useMemo<Record<string, string>>(() => {
    if (!drivers?.results) return {};
    return Object.fromEntries(
      drivers.results.map((driver) => [
        driver.id,
        formatFullName(driver) || `Driver ${driver.id}`,
      ])
    );
  }, [drivers]);

  const columns = useMemo(
    () => createColumns(driverNameLookup, passengerId, drivers?.results ?? []),
    [driverNameLookup, passengerId, drivers?.results]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between pr-4">
        <SectionHeading
          heading="Driver Preferences"
          subHeading="Manage driver preferences"
        />
        <Button variant="secondary" onClick={openModal}>
          Add Driver Preference
        </Button>
      </div>

      {isLoadingPreferences || isLoadingDrivers ? (
        <div className="flex justify-center py-8">
          <LoadingIndicator />
        </div>
      ) : (
        <DataGrid<PreferredDriverRead>
          tableId="preferred-driver"
          dataTestId="preferred-driver-table"
          containerClassName="max-h-[220px]"
          columns={columns}
          data={preferences?.results ?? []}
          emptyState={<PreferredDriverEmptyState />}
          getRowId={(row) => row.id}
          totalCount={preferences?.count ?? 0}
          includePagination={false}
        />
      )}

      <PreferredDriverFormModal
        open={open}
        setOpen={setOpen}
        passengerId={passengerId}
        drivers={drivers?.results ?? []}
      />
    </div>
  );
}
